import React from 'react';
import { useSelector } from 'react-redux';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { FormattedMessage } from 'react-intl';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';
import { Link } from '@bibliocommons/base-links';
import { ShelfMap } from '@bibliocommons/base-icons';
import { METRIC_UNIT } from 'app/constants/EventsConstants';
import { selectImageEntities } from 'app/selectors/EntitiesSelector';
import { selectDistances, selectSystemOfMeasurement } from 'app/selectors/locations/LocationsSearchSelector';
import Svg from '@bibliocommons/svg';

import './LocationImage.scss';

const CONVERT_KM = 1000;
const CONVERT_MILES = 1609.344;
const UNIT_METRIC = 'kilometres';
const UNIT_IMPERIAL = 'miles';

export default function LocationImage({ location }) {
  const images = useSelector(selectImageEntities);
  const distances = useSelector(selectDistances);
  const systemOfMeasurement = useSelector(selectSystemOfMeasurement);
  const imageUrl = images.getIn([location.get('imageId'), 'url']);

  function renderImage() {
    if (imageUrl) {
      return (
        <Link href={location.get('webUrl')} dataKey="location-image" tabIndex="-1" aria-hidden>
          <div className="location-image" aria-hidden>
            <img src={imageUrl} alt="" loading="lazy" />
          </div>
        </Link>
      );
    }
    return (
      <div className="location-image" aria-hidden>
        <div className="location-placeholder-container" aria-hidden>
          <Svg
            className="locations-placeholder-fill"
            width="100%"
            height="100%"
            viewBox="0 0 400 200"
            preserveAspectRatio="none"
          >
            <rect x="-1" y="-1" width="402" height="200" fill="#F0F0F0" stroke="#CCCCCC"></rect>
          </Svg>
          <Svg className="locations-placeholder" width="54" height="48" viewBox="0 0 54 48" preserveAspectRatio="none">
            <path
              d="M0.333496 48V0H26.3335V11H53.6668V48H0.333496ZM4.3335 44H11.3335V37H4.3335V44ZM4.3335 33H11.3335V26H4.3335V33ZM4.3335 22H11.3335V15H4.3335V22ZM4.3335 11H11.3335V4H4.3335V11ZM15.3335 44H22.3335V37H15.3335V44ZM15.3335 33H22.3335V26H15.3335V33ZM15.3335 22H22.3335V15H15.3335V22ZM15.3335 11H22.3335V4H15.3335V11ZM26.3335 44H49.6668V15H26.3335V22H31.6668V26H26.3335V33H31.6668V37H26.3335V44ZM38.6668 26V22H42.6668V26H38.6668ZM38.6668 37V33H42.6668V37H38.6668Z"
              fill="#CCCCCC"
            />
          </Svg>
        </div>
      </div>
    );
  }

  function renderDistance() {
    if (!distances) return null;

    let unit = UNIT_IMPERIAL;
    let distance = distances.find(d => d.get('instanceId') === location.get('id')).get('distance');

    if (systemOfMeasurement === METRIC_UNIT) {
      distance = (distance / CONVERT_KM).toFixed(1);
      unit = UNIT_METRIC;
    } else {
      distance = (distance / CONVERT_MILES).toFixed(1);
    }

    return (
      <div className="location-distance">
        <ShelfMap className="distance-icon" />
        <span className="distance-text" aria-hidden>
          <FormattedMessage id={`distance_away_${unit}`} values={{ distance }} />
        </span>
        <ScreenReaderMessage>
          <FormattedMessage id={`sr_distance_away_${unit}`} values={{ distance }} />
        </ScreenReaderMessage>
      </div>
    );
  }

  return (
    <div className="cp-location-image">
      {renderImage()}
      {renderDistance()}
    </div>
  );
}

LocationImage.propTypes = {
  location: ImmutablePropTypes.map.isRequired
};
